import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { reEngagementAPI } from '../../services/api';

/**
 * Re-engagement — NEW admin tab (Phase 2, items 3/4/9/10). Configure proactive
 * win-back / first-order / browse-no-order pushes (send-time optimized, frequency-
 * capped, holdout-measured, discount only for discount-sensitive users). Additive.
 */

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const num = (n) => (n ?? 0).toLocaleString('en-IN');
const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' };
const kpi = { ...card, textAlign: 'center' };

function monthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { startDate: iso(start), endDate: iso(now) };
}
const CAMP_LABEL = { winback: 'Win-back (lapsed)', first_order: 'First-order (new)', browse_no_order: 'Browsed, no order' };

function ReEngagement() {
  const [range] = useState(monthRange());
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, st] = await Promise.all([reEngagementAPI.getSettings(), reEngagementAPI.getStats(range)]);
      setCfg(s.data.data); setStats(st.data.data);
    } catch (e) { setMsg('Failed to load'); } finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try { const r = await reEngagementAPI.updateSettings(patch); setCfg(r.data.data); setMsg('Saved'); }
    catch (e) { setMsg(e.response?.data?.message || 'Save failed'); }
  };

  if (loading || !cfg) return (<div><Navbar /><div style={{ padding: 24 }}>🔄 Loading…</div></div>);
  const exp = stats?.experiment || {};

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        <h1 style={{ marginBottom: 4 }}>🔔 Re-engagement</h1>
        <p style={{ color: '#777', marginTop: 0 }}>Bring lapsed / never-ordered users back with targeted, send-time-optimized pushes. Holdout measures true lift.</p>
        {msg && <div style={{ ...card, borderColor: '#10B981', color: '#059669', marginBottom: 16 }}>{msg}</div>}

        <div style={{ ...card, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => save({ enabled: e.target.checked })} />
            <b>Engine {cfg.enabled ? 'ON' : 'OFF'}</b>
          </label>
          <span style={{ fontSize: 12, color: '#999' }}>{cfg.enabled ? 'Sending re-engagement pushes.' : 'Off — turn on once you have a coupon + are ready.'}</span>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 8 }}>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Touches</div><div style={{ fontSize: 22, fontWeight: 700 }}>{num(stats?.totalTouches)}</div></div>
          <div style={kpi}><div style={{ color: '#777', fontSize: 12 }}>Recovered Revenue</div><div style={{ fontSize: 22, fontWeight: 700, color: '#059669' }}>{fmt(stats?.recoveredRevenue)}</div></div>
          {(stats?.byCampaign || []).map((c) => (
            <div style={kpi} key={c.campaign}><div style={{ color: '#777', fontSize: 12 }}>{CAMP_LABEL[c.campaign] || c.campaign}</div><div style={{ fontSize: 22, fontWeight: 700 }}>{num(c.touches)}</div></div>
          ))}
        </div>

        <div style={{ ...card, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Measured Lift (holdout)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div><div style={{ color: '#888', fontSize: 12 }}>Treatment</div><b>{exp.treatmentConversionPct != null ? `${exp.treatmentConversionPct}%` : '—'}</b> <span style={{ fontSize: 11, color: '#999' }}>of {num(exp.treatmentTouches)}</span></div>
            <div><div style={{ color: '#888', fontSize: 12 }}>Control (holdout)</div><b>{exp.controlConversionPct != null ? `${exp.controlConversionPct}%` : '—'}</b> <span style={{ fontSize: 11, color: '#999' }}>of {num(exp.controlTouches)}</span></div>
            <div><div style={{ color: '#888', fontSize: 12 }}>Incremental Lift</div><b style={{ color: (exp.measuredLiftPct || 0) >= 0 ? '#059669' : '#EF4444', fontSize: 18 }}>{exp.measuredLiftPct != null ? `${exp.measuredLiftPct > 0 ? '+' : ''}${exp.measuredLiftPct} pts` : '—'}</b></div>
          </div>
        </div>

        <div style={card}>
          <h3 style={{ marginTop: 0 }}>Configuration</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16 }}>
            <label>Holdout % (control)
              <input type="number" min="0" max="90" defaultValue={cfg.holdoutPercent} onBlur={(e) => save({ holdoutPercent: Number(e.target.value) })} style={{ width: '100%' }} />
            </label>
            <label>Offer coupon (incentive stage)
              <input defaultValue={cfg.offerCouponCode} onBlur={(e) => save({ offerCouponCode: e.target.value })} style={{ width: '100%' }} />
            </label>
            <label>Min days between touches
              <input type="number" min="1" defaultValue={cfg.minDaysBetweenTouches} onBlur={(e) => save({ minDaysBetweenTouches: Number(e.target.value) })} style={{ width: '100%' }} />
            </label>
            <label>Send-time fallback hour (0–23)
              <input type="number" min="0" max="23" defaultValue={cfg.sendHourFallback} onBlur={(e) => save({ sendHourFallback: Number(e.target.value) })} style={{ width: '100%' }} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={cfg.respectSendTime} onChange={(e) => save({ respectSendTime: e.target.checked })} /> Send at user's typical order hour
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={cfg.discountOnlyForSensitive} onChange={(e) => save({ discountOnlyForSensitive: e.target.checked })} /> Discount only for discount-sensitive users
            </label>
          </div>
          <h4 style={{ marginBottom: 8 }}>Campaigns</h4>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={cfg.winbackEnabled} onChange={(e) => save({ winbackEnabled: e.target.checked })} /> Win-back (lapsed 3–30d)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={cfg.firstOrderEnabled} onChange={(e) => save({ firstOrderEnabled: e.target.checked })} /> First-order (new, never ordered)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={cfg.browseNoOrderEnabled} onChange={(e) => save({ browseNoOrderEnabled: e.target.checked })} /> Browsed, no order <span style={{ fontSize: 11, color: '#999' }}>(needs events build live)</span>
            </label>
          </div>

          {/* WhatsApp channel — reaches churned users who uninstalled (no push token) */}
          <h4 style={{ marginBottom: 4, marginTop: 20 }}>📲 WhatsApp channel</h4>
          <p style={{ fontSize: 12, color: '#777', marginTop: 0 }}>
            Lapsed users who uninstalled have no push token, so a push touch reaches no one. With WhatsApp on, those touches go over WhatsApp instead (needs MSG91 WhatsApp configured + a Meta-approved template). Template variables: <code>{'{{1}}'}</code>=name{cfg.offerCouponCode ? <>, <code>{'{{2}}'}</code>=coupon</> : ' (add a coupon above to also send {{2}}=coupon)'}.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={!!cfg.whatsappEnabled} onChange={(e) => save({ whatsappEnabled: e.target.checked })} /> Enable WhatsApp channel
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={cfg.whatsappFallbackOnly !== false} onChange={(e) => save({ whatsappFallbackOnly: e.target.checked })} /> Only when no push token <span style={{ fontSize: 11, color: '#999' }}>(off = WhatsApp for all lapsed)</span>
            </label>
            <label>Approved template name
              <input defaultValue={cfg.whatsappTemplateName} onBlur={(e) => save({ whatsappTemplateName: e.target.value })} placeholder="e.g. winback_miss_you" style={{ width: '100%' }} />
            </label>
            <label>Template language code
              <input defaultValue={cfg.whatsappLanguageCode || 'en'} onBlur={(e) => save({ whatsappLanguageCode: e.target.value })} placeholder="en" style={{ width: '100%' }} />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReEngagement;
